import React from 'react';
import { motion } from 'framer-motion';
import { HiLightningBolt, HiChip, HiMail, HiShoppingCart, HiCode, HiArrowLeft } from 'react-icons/hi';
import { useParams, Link } from 'react-router-dom';

const ResearchDetail = () => {
  const { id } = useParams();
  
  console.log('Research ID from URL:', id);

  const researchData = {
    'jarviss-ai-brain': {
      title: 'JARVIS AI Brain',
      subtitle: 'Advanced Neural Network Architecture',
      description: 'An innovative approach to artificial intelligence that combines multiple neural network architectures to create a more intelligent and adaptive AI system.',
      image: '/images/3.png',
      color: 'accent-green',
      icon: HiChip,
      sections: [
        {
          title: 'Overview',
          content: 'The JARVIS AI Brain project explores the development of a multi-layered neural network architecture that mimics human cognitive processes. This research focuses on creating an AI system capable of learning, reasoning, and adapting to new situations in real-time.',
          features: [
            'Multi-layered neural architecture',
            'Real-time learning capabilities',
            'Adaptive reasoning systems',
            'Cognitive process modeling'
          ]
        },
        {
          title: 'Technical Architecture',
          content: 'The system utilizes a combination of convolutional neural networks (CNNs), recurrent neural networks (RNNs), and transformer models to process and understand complex patterns in data.',
          features: [
            'CNN for pattern recognition',
            'RNN for sequential processing',
            'Transformer for attention mechanisms',
            'Hybrid model integration'
          ]
        },
        {
          title: 'Applications',
          content: 'This research has potential applications in autonomous systems, natural language processing, computer vision, and decision-making systems.',
          features: [
            'Autonomous vehicle systems',
            'Natural language processing',
            'Computer vision applications',
            'Decision support systems'
          ]
        }
      ]
    },
    'jarvis-food-ordering': {
      title: 'JARVIS Food Ordering System',
      subtitle: 'Intelligent Restaurant Management',
      description: 'An AI-powered food ordering system that revolutionizes the restaurant industry by providing intelligent recommendations and automated order processing.',
      image: '/images/embt.png',
      color: 'accent-blue',
      icon: HiShoppingCart,
      sections: [
        {
          title: 'System Overview',
          content: 'The JARVIS Food Ordering System integrates artificial intelligence with restaurant management to create a seamless dining experience. The system learns customer preferences and provides personalized recommendations.',
          features: [
            'AI-powered recommendations',
            'Personalized menu suggestions',
            'Automated order processing',
            'Customer preference learning'
          ]
        },
        {
          title: 'Key Features',
          content: 'The system includes advanced features such as natural language processing for order understanding, machine learning for preference prediction, and real-time inventory management.',
          features: [
            'Natural language order processing',
            'Preference prediction algorithms',
            'Real-time inventory tracking',
            'Multi-language support'
          ]
        },
        {
          title: 'Business Impact',
          content: 'This system has shown significant improvements in order accuracy, customer satisfaction, and operational efficiency for participating restaurants.',
          features: [
            'Increased order accuracy',
            'Improved customer satisfaction',
            'Reduced wait times',
            'Enhanced operational efficiency'
          ]
        }
      ]
    },
    'jarvis-email-capability': {
      title: 'JARVIS Email Intelligence',
      subtitle: 'Smart Email Management System',
      description: 'An intelligent email management system that uses AI to categorize, prioritize, and respond to emails automatically, improving productivity and communication efficiency.',
      image: '/images/ITv-Logo.png',
      color: 'accent-green',
      icon: HiMail,
      sections: [
        {
          title: 'Email Intelligence',
          content: 'The JARVIS Email Intelligence system uses natural language processing and machine learning to understand email content, categorize messages, and provide intelligent responses.',
          features: [
            'Automatic email categorization',
            'Priority-based sorting',
            'Smart response suggestions',
            'Spam detection and filtering'
          ]
        },
        {
          title: 'Advanced Features',
          content: 'The system includes advanced features such as sentiment analysis, intent recognition, and automated follow-up scheduling based on email content and context.',
          features: [
            'Sentiment analysis',
            'Intent recognition',
            'Automated follow-ups',
            'Context-aware responses'
          ]
        },
        {
          title: 'Productivity Benefits',
          content: 'Users have reported significant time savings and improved email management efficiency, with the system handling routine communications automatically.',
          features: [
            'Time savings on email management',
            'Improved response times',
            'Better organization',
            'Reduced email overload'
          ]
        }
      ]
    }
  };

  const research = researchData[id];
  
  console.log('Available research IDs:', Object.keys(researchData));
  console.log('Looking for research with ID:', id);
  console.log('Found research:', research);

  if (!research) {
    return (
      <div className="min-h-screen pt-32 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-morphism-strong rounded-2xl p-8">
            <h1 className="text-display-3 font-space-grotesk font-bold gradient-text-green mb-6">
              Research Not Found
            </h1>
            <p className="text-gray-300 mb-6">The requested research project could not be found.</p>
            <Link to="/" className="btn-primary">
              Return Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const IconComponent = research.icon;

  return (
    <div className="min-h-screen px-6 pb-20">
      <div className="max-w-6xl mx-auto pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Back Button */}
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center text-accent-green hover:text-accent-green/80 transition-colors">
              <HiArrowLeft className="mr-2" />
              Back to Home
            </Link>
          </div>
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 glass-morphism px-6 py-3 rounded-full mb-6">
              <IconComponent className={`text-${research.color}`} />
              <span className={`font-jetbrains text-${research.color} tracking-wider text-sm`}>
                RESEARCH PROJECT
              </span>
            </div>
            <h1 className="text-display-2 font-space-grotesk font-bold mb-4">
              <span className="text-white">{research.title}</span>
            </h1>
            <h2 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6">
              {research.subtitle}
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              {research.description}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
                         {/* Main Content */}
             <div className="lg:col-span-2 space-y-8">
               {/* Research Sections */}
              {research.sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="glass-morphism-strong rounded-2xl p-8"
                >
                  <h3 className="text-2xl font-space-grotesk font-bold gradient-text-green mb-6">
                    {section.title}
                  </h3>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {section.content}
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {section.features.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: featureIndex * 0.1 }}
                        className="flex items-center space-x-3"
                      >
                        <HiLightningBolt className={`text-${research.color} text-lg`} />
                        <span className="text-gray-300">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Project Info */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-morphism-strong rounded-2xl p-6"
              >
                <div className="text-center">
                  <div className={`w-16 h-16 bg-${research.color}/20 rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <IconComponent className={`text-2xl text-${research.color}`} />
                  </div>
                  <h3 className="text-xl font-space-grotesk font-bold text-white mb-2">
                    Research Project
                  </h3>
                  <p className="text-gray-300 text-sm">
                    AI & Machine Learning Research
                  </p>
                </div>
              </motion.div>

              {/* Quick Stats */}
              <div className="glass-morphism-strong rounded-2xl p-6">
                <h4 className="text-lg font-space-grotesk font-bold text-white mb-4">
                  Project Stats
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span className="text-accent-green">Active</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Category:</span>
                    <span className="text-white">AI Research</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Technology:</span>
                    <span className="text-white">Machine Learning</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Year:</span>
                    <span className="text-white">2024</span>
                  </div>
                </div>
              </div>

              {/* Back to Research */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="glass-morphism-strong rounded-2xl p-6"
              >
                <Link to="/#research" className="block text-center">
                  <span className="text-accent-green hover:text-accent-green/80 transition-colors flex items-center justify-center">
                    <HiArrowLeft className="mr-2" />
                    Back to Research
                  </span>
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResearchDetail; 